
import { useState, useEffect } from 'react';
import Layout from '@/components/layout/Layout';
import ChatbotCard from '@/components/chat/ChatbotCard';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Search } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { ChatbotData } from '@/components/chat/chatTypes';

const Explore = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [allChatbots, setAllChatbots] = useState<ChatbotData[]>([]);
  const [allTags, setAllTags] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Fetch agents from Supabase
  useEffect(() => {
    const fetchAgents = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from('agents')
          .select('*');
        
        if (error) {
          throw error;
        }
        
        // Transform data to match ChatbotData interface
        const formattedChatbots: ChatbotData[] = data.map((agent: any) => ({
          id: agent.id,
          name: agent.name,
          description: agent.description,
          tags: agent.tags,
          avatarUrl: agent.avatar_url,
          conversations: agent.conversations
        }));
        
        setAllChatbots(formattedChatbots);
        
        // Extract all unique tags
        const tagsSet = new Set<string>();
        formattedChatbots.forEach(chatbot => {
          chatbot.tags.forEach(tag => tagsSet.add(tag));
        });
        
        setAllTags(Array.from(tagsSet));
      } catch (error) {
        console.error('Error fetching agents:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchAgents();
  }, []);
  
  const toggleTag = (tag: string) => {
    setSelectedTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag) 
        : [...prev, tag]
    );
  };
  
  const filteredChatbots = allChatbots.filter(chatbot => {
    // Filter by search query
    const matchesSearch = chatbot.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          chatbot.description.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Filter by selected tags
    const matchesTags = selectedTags.length === 0 || 
                        selectedTags.some(tag => chatbot.tags.includes(tag));
    
    return matchesSearch && matchesTags;
  });

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-display font-bold mb-4">Explore AI Personas</h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Discover our collection of unique AI personalities and start a conversation
          </p>
        </div>
        
        {/* Search and Filter */}
        <div className="mb-8 space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={18} />
            <Input 
              placeholder="Search by name or description..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 rounded-full"
            />
          </div>
          
          <div>
            <p className="text-sm font-medium mb-2">Filter by tags:</p>
            <div className="flex flex-wrap gap-2">
              {allTags.map(tag => (
                <Badge 
                  key={tag}
                  variant={selectedTags.includes(tag) ? "default" : "outline"}
                  className="cursor-pointer rounded-full"
                  onClick={() => toggleTag(tag)}
                >
                  {tag}
                </Badge>
              ))}
            </div>
          </div>
        </div>
        
        {/* Results */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 animate-fade-in">
          {isLoading ? (
            <div className="col-span-3 py-12 text-center">
              <div className="inline-block animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
              <p className="mt-4 text-muted-foreground">Loading AI personas...</p>
            </div>
          ) : filteredChatbots.length > 0 ? (
            filteredChatbots.map((chatbot) => (
              <ChatbotCard
                key={chatbot.id}
                id={chatbot.id}
                name={chatbot.name}
                description={chatbot.description}
                tags={chatbot.tags}
                avatarUrl={chatbot.avatarUrl}
                conversations={chatbot.conversations}
              />
            ))
          ) : (
            <div className="col-span-3 py-12 text-center">
              <p className="text-muted-foreground">No chatbots found matching your search criteria.</p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Explore;
